import React, { useState, useEffect, useRef } from 'react';
import './Mp3PlayerWidget.css';

const SVGIcons = {
    Play: () => (
        <svg width="36" height="36" viewBox="0 0 24 24" fill="currentColor">
            <path d="M8 5v14l11-7z" />
        </svg>
    ),
    Pause: () => (
        <svg width="36" height="36" viewBox="0 0 24 24" fill="currentColor">
            <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
        </svg>
    ),
    Replay: () => (
        <svg width="36" height="36" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 5V1L7 6l5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z" />
        </svg>
    ),
    VolumeUp: () => (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
        </svg>
    ),
    VolumeOff: () => (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z" />
        </svg>
    ),
    FastRewind: () => (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M11 18V6l-8.5 6 8.5 6zm.5-6l8.5 6V6l-8.5 6z" />
        </svg>
    ),
    FastForward: () => (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M4 18l8.5-6L4 6v12zm9-12v12l8.5-6L13 6z" />
        </svg>
    ),
    Warning: () => (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
        </svg>
    )
};

const formatDuration = (timeInSeconds) => {
    if (isNaN(timeInSeconds)) return '00:00';
    const minutes = Math.floor(timeInSeconds / 60).toString().padStart(2, '0');
    const seconds = Math.floor(timeInSeconds % 60).toString().padStart(2, '0');
    return `${minutes}:${seconds}`;
};

const speeds = [0.5, 0.75, 1.0, 1.25, 1.5, 2.0];

const Mp3PlayerWidget = ({ audioUrl, title, subtitle, imageUrl }) => {
    const audioRef = useRef(null);

    const [isPlaying, setIsPlaying] = useState(false);
    const [isCompleted, setIsCompleted] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [volume, setVolume] = useState(1.0);
    const [speed, setSpeed] = useState(1.0);
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState(null);

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        const setAudioData = () => {
            setDuration(audio.duration);
            setIsLoading(false);
        };

        const setAudioTime = () => setCurrentTime(audio.currentTime);

        const handleEnded = () => {
            setIsPlaying(false);
            setIsCompleted(true);
        };

        const handleError = (e) => {
            setIsLoading(false);
            setErrorMessage('Ses dosyası yüklenemedi.');
        };

        const handleCanPlay = () => {
            setIsLoading(false);
        };

        audio.addEventListener('loadedmetadata', setAudioData);
        audio.addEventListener('timeupdate', setAudioTime);
        audio.addEventListener('ended', handleEnded);
        audio.addEventListener('error', handleError);
        audio.addEventListener('canplay', handleCanPlay);

        setIsLoading(true);
        setIsCompleted(false);

        return () => {
            audio.removeEventListener('loadedmetadata', setAudioData);
            audio.removeEventListener('timeupdate', setAudioTime);
            audio.removeEventListener('ended', handleEnded);
            audio.removeEventListener('error', handleError);
            audio.removeEventListener('canplay', handleCanPlay);
        };
    }, [audioUrl]);

    const togglePlayPause = () => {
        const audio = audioRef.current;
        if (!audio) return;

        if (isCompleted) {
            audio.currentTime = 0;
            audio.play();
            setIsPlaying(true);
            setIsCompleted(false);
        } else if (isPlaying) {
            audio.pause();
            setIsPlaying(false);
        } else {
            audio.play();
            setIsPlaying(true);
        }
    };

    const toggleMute = () => {
        const audio = audioRef.current;
        if (!audio) return;

        if (volume > 0) {
            audio.volume = 0;
            setVolume(0);
        } else {
            audio.volume = 1;
            setVolume(1);
        }
    };

    const skipBackward = () => {
        const audio = audioRef.current;
        if (!audio) return;
        audio.currentTime = Math.max(0, audio.currentTime - 10);
        if (isCompleted) setIsCompleted(false);
    };

    const skipForward = () => {
        const audio = audioRef.current;
        if (!audio) return;
        audio.currentTime = Math.min(duration, audio.currentTime + 10);
    };

    const cycleSpeed = () => {
        const audio = audioRef.current;
        if (!audio) return;
        const currentIndex = speeds.indexOf(speed);
        const nextIndex = (currentIndex + 1) % speeds.length;
        const newSpeed = speeds[nextIndex];
        audio.playbackRate = newSpeed;
        setSpeed(newSpeed);
    };

    const handleSeek = (e) => {
        const audio = audioRef.current;
        if (!audio) return;
        const newTime = Number(e.target.value);
        audio.currentTime = newTime;
        setCurrentTime(newTime);
        if (isCompleted && newTime < duration) {
            setIsCompleted(false);
        }
    };

    const sliderProgress = duration > 0 ? (currentTime / duration) * 100 : 0;

    if (!audioUrl) return null;

    return (
        <div className="mp3-player-widget">
            <audio ref={audioRef} src={audioUrl} preload="metadata" />

            {isLoading ? (
                <div className="mp3-loading-container">
                    <div className="mp3-spinner"></div>
                </div>
            ) : errorMessage ? (
                <div className="mp3-error-container">
                    <SVGIcons.Warning />
                    <p>{errorMessage}</p>
                </div>
            ) : (
                <div className="mp3-player-content">
                    <div className="mp3-cover-container">
                        {imageUrl ? (
                            <img src={imageUrl} alt={title || "Cover"} className="mp3-cover" />
                        ) : (
                            <div className="mp3-cover mp3-default-cover">
                                <svg width="48" height="48" viewBox="0 0 24 24" fill="rgba(255,255,255,0.8)">
                                    <path d="M12 3v9.28a4.39 4.39 0 0 0-1.5-.28C8.01 12 6 13.79 6 16s2.01 4 4.5 4S15 18.21 15 16V6h4V3h-7z" />
                                </svg>
                            </div>
                        )}
                    </div>

                    {(title || subtitle) && (
                        <div className="mp3-text-info">
                            {title && <h3 className="mp3-title" title={title}>{title}</h3>}
                            {subtitle && <p className="mp3-subtitle" title={subtitle}>{subtitle}</p>}
                        </div>
                    )}

                    <div className="mp3-controls-row">
                        <button className="mp3-dashed-btn size-46" onClick={toggleMute} aria-label="Mute/Unmute">
                            <div className="mp3-icon-wrapper">
                                {volume === 0 ? <SVGIcons.VolumeOff /> : <SVGIcons.VolumeUp />}
                            </div>
                        </button>

                        <button className="mp3-dashed-btn size-50" onClick={skipBackward} aria-label="Skip backward 10 seconds">
                            <div className="mp3-skip-content">
                                <SVGIcons.FastRewind />
                                <span className="mp3-skip-text">10</span>
                            </div>
                        </button>

                        <button className="mp3-play-btn" onClick={togglePlayPause} aria-label="Play/Pause">
                            {isCompleted
                                ? <SVGIcons.Replay />
                                : isPlaying ? <SVGIcons.Pause /> : <SVGIcons.Play />
                            }
                        </button>

                        <button className="mp3-dashed-btn size-50" onClick={skipForward} aria-label="Skip forward 10 seconds">
                            <div className="mp3-skip-content">
                                <SVGIcons.FastForward />
                                <span className="mp3-skip-text">10</span>
                            </div>
                        </button>

                        <button className="mp3-dashed-btn size-46" onClick={cycleSpeed} aria-label="Change playback speed">
                            <span className="mp3-speed-text">{speed}x</span>
                        </button>
                    </div>

                    <div className="mp3-seek-container">
                        <input
                            type="range"
                            min={0}
                            max={duration || 1}
                            value={currentTime}
                            onChange={handleSeek}
                            className="mp3-seek-slider"
                            style={{
                                background: `linear-gradient(to right, var(--mp3-magenta) ${sliderProgress}%, var(--mp3-track-bg) ${sliderProgress}%)`
                            }}
                        />
                        <div className="mp3-time-display">
                            <span>{formatDuration(currentTime)} / {formatDuration(duration)}</span>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Mp3PlayerWidget;
